/**
 * Утилиты для работы с СНИЛС
 */

/**
 * Проверяет, что строка является валидным номером СНИЛС.
 * Проверяет контрольную сумму и формат «девять цифр + две цифры».
 * 
 * @param value - Строка с номером СНИЛС (может содержать дефисы и пробелы)
 * @returns true, если СНИЛС валиден, false в противном случае
 */
export const isValidSnils = (value?: string): boolean => {
    if (!value) return true; // Пустое значение считается валидным (поле необязательное)
    
    // Оставляем только цифры
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 11) return false;

    // payload: первые 9 цифр
    const nums = digits
        .slice(0, 9)
        .split('')
        .map(d => +d);
    
    // checksum: последние 2 цифры
    const checksum = +digits.slice(9);
    
    // Считаем сумму: payload[i] * (9 - i)
    const sum = nums.reduce((acc, n, i) => acc + n * (9 - i), 0);
    let expected = sum % 101;
    if (expected === 100) expected = 0;

    return expected === checksum;
};

/**
 * Валидатор для react-hook-form, проверяющий формат СНИЛС
 * 
 * @param value - Значение поля
 * @returns true, если валидно, или строку с сообщением об ошибке
 */
export const validateSnilsFormat = (value?: string): true | string => {
    if (!value) return true; // Пустое значение считается валидным
    
    // Проверка формата с дефисами и пробелом
    const formatPattern = /^\d{3}-\d{3}-\d{3} \d{2}$/;
    if (!formatPattern.test(value)) {
        return 'Неверный формат СНИЛС';
    }
    
    return true;
};

/**
 * Валидатор для react-hook-form, проверяющий контрольную сумму СНИЛС
 * 
 * @param value - Значение поля
 * @returns true, если валидно, или строку с сообщением об ошибке
 */
export const validateSnilsChecksum = (value?: string): true | string => {
    if (!value) return true; // Пустое значение считается валидным
    
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 11) {
        return 'Неверный формат СНИЛС';
    }

    if (!isValidSnils(value)) {
        return 'СНИЛС содержит ошибку - неверная контрольная сумма';
    }
    
    return true;
};

/**
 * Полный валидатор СНИЛС для react-hook-form (формат + контрольная сумма)
 * 
 * @param value - Значение поля
 * @returns true, если валидно, или строку с сообщением об ошибке
 */
export const validateSnils = (value?: string): true | string => {
    if (!value) return true; // Пустое значение считается валидным
    
    const formatError = validateSnilsFormat(value);
    if (formatError !== true) {
        return formatError;
    }
    
    return validateSnilsChecksum(value);
};

/**
 * Правила валидации СНИЛС для react-hook-form
 */
export const snilsValidationRules = {
    pattern: {
        value: /^\d{3}-\d{3}-\d{3} \d{2}$/,
        message: 'Неверный формат СНИЛС',
    },
    validate: validateSnils,
};

